import React, { useEffect, useMemo, useRef, useState } from 'react';
import PopoverLayer from './popoverLayer';
import { useInlineValidation, InlineFieldError } from './inlineValidation';

const normalizeOption = (option, index) => {
  if (typeof option === 'string' || typeof option === 'number') {
    return { value: String(option), label: String(option), key: `${option}-${index}` };
  }

  // Same precedence the plain <select> used before. CostCenterID is deliberately
  // not a value fallback: branch rows carry both fields and every caller matches
  // the selection back on BranchNickname.
  const hasExplicitValue = option.value !== undefined && option.value !== null;
  const optionValue = hasExplicitValue
    ? option.value
    : (option.Title || option.BranchNickname || option.displayName || '');
  const optionLabel = option.label || option.Title || option.BranchNickname || option.displayName || optionValue;
  return { value: String(optionValue), label: String(optionLabel), key: `${optionValue}-${index}` };
};

// `placeholder` overrides the default "Select <label>" text, and `hideLabel` drops
// the floating label for callers that already render their own <label> above the
// field. `compact` shrinks the trigger to button height (36px) for toolbars.
// All default to the old behaviour, so existing call sites are unaffected.
const FloatingLabelSelect = ({ label, options = [], handleChange, value = '', name, id, required = false, disabled = false, placeholder = '', hideLabel = false, compact = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const buttonRef = useRef(null);
  const rootRef = useRef(null);
  // Inline message instead of the browser's "Please select an item in the list." bubble.
  const { message: invalidMessage, onInvalid: handleInvalid } = useInlineValidation({
    label, value, kind: 'select', focusRef: buttonRef, scrollRef: rootRef,
  });
  const invalid = Boolean(invalidMessage);
  const panelRef = useRef(null);
  const normalizedOptions = useMemo(() => options.map(normalizeOption), [options]);
  const selectedIndex = normalizedOptions.findIndex(option => option.value === String(value ?? ''));
  const selectedOption = normalizedOptions[selectedIndex];

  useEffect(() => {
    if (!isOpen) return undefined;
    // The list lives in a body portal, so it is outside rootRef — it has to count as
    // "inside" or mousedown would close the list before an option's click landed.
    const handleOutside = event => {
      const insideField = rootRef.current?.contains(event.target);
      const insidePanel = panelRef.current?.contains(event.target);
      if (!insideField && !insidePanel) setIsOpen(false);
    };
    // Capture phase + stopPropagation so Escape closes only the list. ModalShell
    // listens for Escape on document too, and without this the surrounding modal
    // would close along with the list.
    const handleKey = event => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleKey, true);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleKey, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) setActiveIndex(Math.max(selectedIndex, 0));
  }, [isOpen, selectedIndex]);

  const selectOption = option => {
    handleChange?.({ target: { name, id, value: option.value } });
    setIsOpen(false);
  };

  const handleKeyDown = event => {
    if (disabled) return;
    if (event.key === 'Escape') {
      setIsOpen(false);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        return;
      }
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex(index => (index + direction + normalizedOptions.length) % normalizedOptions.length);
    }
    if ((event.key === 'Enter' || event.key === ' ') && isOpen) {
      event.preventDefault();
      if (normalizedOptions[activeIndex]) selectOption(normalizedOptions[activeIndex]);
    }
  };

  return (
    <div>
    <div className="relative" ref={rootRef}>
      <select
        tabIndex={-1}
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
        id={`${id || name || 'select'}-native`}
        name={name}
        value={String(value ?? '')}
        onChange={handleChange}
        required={required}
        disabled={disabled}
        onInvalid={handleInvalid}
      >
        {normalizedOptions.map(option => <option key={option.key} value={option.value}>{option.label}</option>)}
      </select>

      <button
        type="button"
        id={id}
        ref={buttonRef}
        onClick={() => !disabled && setIsOpen(open => !open)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={`flex w-full items-center justify-between gap-3 ${compact ? 'min-h-9 rounded-lg py-2' : 'min-h-12 rounded-xl'} border bg-white px-3.5 text-left text-sm shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-orange-300 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 ${isOpen ? 'border-[#fb6635] ring-1 ring-[#fb6635]' : invalid ? 'border-red-400 ring-1 ring-red-200' : 'border-slate-300 hover:border-slate-400'}`}
      >
        <span className={selectedOption?.value ? 'font-medium text-[#211b4f]' : 'text-slate-400'}>
          {selectedOption?.label || placeholder || `Select ${label}`}
        </span>
        <svg className={`h-4 w-4 flex-none text-slate-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {!hideLabel && (
        <label htmlFor={id} className="pointer-events-none absolute -top-2 left-3 z-10 bg-white px-1 text-[11px] font-medium text-slate-500">
          {label}{required && <span className="text-red-600">*</span>}
        </label>
      )}

      {isOpen && (
        <PopoverLayer anchorRef={rootRef} panelRef={panelRef} matchAnchorWidth>
        <div ref={panelRef} className="profile-review-scrollbar max-h-60 space-y-1 overflow-y-auto rounded-xl border border-slate-300 bg-white p-1.5 shadow-2xl ring-1 ring-black/5" role="listbox" aria-label={label}>
          {normalizedOptions.map((option, index) => {
            const selected = option.value === String(value ?? '');
            return (
              <button
                key={option.key}
                type="button"
                role="option"
                aria-selected={selected}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => selectOption(option)}
                className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${selected ? 'bg-[#211b4f] font-semibold text-white' : activeIndex === index ? 'bg-orange-50 font-medium text-[#211b4f]' : 'font-medium text-[#211b4f] hover:bg-orange-50'}`}
              >
                <span className="truncate">{option.label}</span>
                {selected && <svg className="h-4 w-4 flex-none" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="m5 12 4 4L19 6" /></svg>}
              </button>
            );
          })}
        </div>
        </PopoverLayer>
      )}
    </div>
    <InlineFieldError message={invalidMessage} />
    </div>
  );
};

export default FloatingLabelSelect;
